import { useState, useEffect, useMemo } from 'react';
import Sidebar from '../../components/common/Sidebar';
import PaginationControls from '../../components/common/PaginationControls';
import DirecteurRoleManagement from '../../components/directeur/DirecteurRoleManagement';
import ValidationInterface from '../../components/chef/ValidationInterface';
import PlanPreviewModal from '../../components/teacher/PlanPreviewModal';
import NoticeToast from '../../components/common/NoticeToast';
import { DonutChart, VerticalBarChart } from '../../components/admin/AdminCharts';
import { getAllPlans, getPlanGrilleData, updateGrilleData } from '../../api/directeur';
import { useAuth } from '../../auth/AuthContext';
import { BRAND_ASSETS } from '../../branding';
import { apiUrl } from '../../config/api';
import { useNotice } from '../../hooks/useNotice';
import { openOrDownloadDocument, safeFilename } from '../../utils/documentHandling';
import './DirecteurDashboard.css';

const ITEMS_PER_PAGE = 10;
const FILE_BASE = apiUrl();

export default function DirecteurDashboard() {
  const { user, logout } = useAuth();
  const { notice, showNotice, clearNotice } = useNotice();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState(0);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [timelinePlan, setTimelinePlan] = useState(null);
  const [timelinePlanData, setTimelinePlanData] = useState(null);
  const [previewPlan, setPreviewPlan] = useState(null);
  const [planPayload, setPlanPayload] = useState(null);
  const [loadingPayload, setLoadingPayload] = useState(false);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [activeSection, setActiveSection] = useState('dashboard');
  const [currentPage, setCurrentPage] = useState(1);
  const [filters, setFilters] = useState({
    teacher: '',
    course: '',
    chef: '',
    status: '',
    session: '',
    programme: '',
    mode: '',
    campus: '',
    sortBy: 'date_soumission',
    sortDirection: 'desc',
  });

  const handleNavigate = (sectionKey) => {
    setActiveSection(sectionKey);
  };

  useEffect(() => {
    const loadPlans = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await getAllPlans();
        setPlans(data);
      } catch (err) {
        setError('Impossible de charger les plans.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, [refreshToken]);

  useEffect(() => {
    const loadPayload = async () => {
      if (!selectedPlan?.id) {
        setPlanPayload(null);
        return;
      }

      setLoadingPayload(true);
      try {
        const data = await getPlanGrilleData(selectedPlan.id);
        setPlanPayload(data.payload || {});
      } catch (err) {
        console.error('Erreur chargement payload direction:', err);
        setPlanPayload({});
      } finally {
        setLoadingPayload(false);
      }
    };

    loadPayload();
  }, [selectedPlan?.id]);

  const sessions = useMemo(() => {
    const unique = new Set(plans.map((plan) => plan.session).filter((value) => value && value !== 'N/A'));
    return ['Toutes', ...Array.from(unique)];
  }, [plans]);

  const programmes = useMemo(() => {
    const unique = new Set(plans.map((plan) => plan.programme).filter((value) => value && value !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [plans]);

  const modes = useMemo(() => {
    const unique = new Set(plans.map((plan) => plan.mode_enseignement).filter((value) => value && value !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [plans]);

  const campus = useMemo(() => {
    const unique = new Set(plans.map((plan) => plan.campus).filter((value) => value && value !== 'N/A'));
    return ['Tous', ...Array.from(unique)];
  }, [plans]);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSortChange = (sortBy, sortDirection) => {
    if (!sortDirection) {
      setFilters((prev) => ({ ...prev, sortBy: 'date_soumission', sortDirection: 'desc' }));
      return;
    }
    setFilters((prev) => ({ ...prev, sortBy, sortDirection }));
  };

  const getSortIndicator = (column) => {
    if (filters.sortBy !== column) {
      return '↕';
    }
    return filters.sortDirection === 'asc' ? '↑' : '↓';
  };

  const handleRefresh = () => {
    setRefreshToken((current) => current + 1);
  };

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handleOpenTimeline = async (plan) => {
    setTimelinePlan(plan);
    setTimelinePlanData(null);
    try {
      const data = await getPlanGrilleData(plan.id);
      setTimelinePlanData(data);
    } catch (err) {
      console.error('Erreur lors du chargement de la chronologie:', err);
    }
  };

  const handleCloseTimeline = () => {
    setTimelinePlan(null);
    setTimelinePlanData(null);
  };

  const handleOpenPreview = (plan) => {
    const planUrl = plan.nom_fichier ? `${FILE_BASE}/static/plans/${plan.id}_${safeFilename(plan.nom_fichier)}` : '#';

    openOrDownloadDocument({
      url: planUrl,
      fileName: plan.nom_fichier,
      onOpenPdf: () => setPreviewPlan({
        id: plan.id,
        title: plan.titre || plan.numero_cours || plan.cours || 'Plan de cours',
        course: plan.numero_cours || plan.cours || 'N/A',
        pdfUrl: planUrl,
      }),
      onNotify: showNotice,
    });
  };

  const toDaDisplayStatus = (statusKey) => {
    if (statusKey === 'non_analyse') {
      return 'analyse';
    }
    return statusKey;
  };

  const filteredPlans = useMemo(() => {
    const filtered = plans.filter((plan) => {
      const displayStatus = toDaDisplayStatus(plan.statusKey || plan.statut);
      if (filters.status && displayStatus !== filters.status) return false;
      if (filters.teacher && !plan.enseignant_nom?.toLowerCase().includes(filters.teacher.toLowerCase())) return false;
      if (filters.course && !plan.titre?.toLowerCase().includes(filters.course.toLowerCase())) return false;
      if (filters.chef && !plan.chef_equipe_nom?.toLowerCase().includes(filters.chef.toLowerCase())) return false;
      if (filters.session && plan.session !== filters.session) return false;
      if (filters.programme && plan.programme !== filters.programme) return false;
      if (filters.mode && plan.mode_enseignement !== filters.mode) return false;
      if (filters.campus && plan.campus !== filters.campus) return false;
      return true;
    });

    const statusRank = {
      analyse: 0,
      'non validé': 1,
      'validé': 2,
      approuvé: 3,
      'non approuvé': 4,
    };

    const compareText = (left, right) => String(left || '').localeCompare(String(right || ''), 'fr', { sensitivity: 'base' });

    return filtered.sort((a, b) => {
      const directionFactor = filters.sortDirection === 'asc' ? 1 : -1;

      if (filters.sortBy === 'date_soumission') {
        const dateA = new Date(a.date_soumission || a.date_upload || 0).getTime();
        const dateB = new Date(b.date_soumission || b.date_upload || 0).getTime();
        if (dateA !== dateB) {
          return (dateA - dateB) * directionFactor;
        }
      } else if (filters.sortBy === 'statusKey') {
        const rankA = statusRank[toDaDisplayStatus(a.statusKey || a.statut)] ?? 999;
        const rankB = statusRank[toDaDisplayStatus(b.statusKey || b.statut)] ?? 999;
        if (rankA !== rankB) {
          return (rankA - rankB) * directionFactor;
        }
      } else {
        const valueA = a[filters.sortBy];
        const valueB = b[filters.sortBy];
        const textCompare = compareText(valueA, valueB);
        if (textCompare !== 0) {
          return textCompare * directionFactor;
        }
      }
      return b.id - a.id;
    });
  }, [plans, filters]);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(filteredPlans.length / ITEMS_PER_PAGE)), [filteredPlans.length]);

  const paginatedPlans = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;
    return filteredPlans.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredPlans, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleSaveGrid = async (payload) => {
    if (!selectedPlan) return;

    setStatusUpdating(true);
    try {
      await updateGrilleData(selectedPlan.id, payload, selectedPlan.statusKey || 'validé');
      showNotice('Decision enregistree avec succes.', 'success');
      setRefreshToken((current) => current + 1);
    } catch (err) {
      showNotice('Erreur lors de l\'enregistrement de la decision.', 'error');
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleStatusChange = async (statut, payload) => {
    if (!selectedPlan) return;

    const recommendation = statut === 'approuvé' ? 'approuvé' : 'rejeté';
    const updatedPayload = {
      ...(payload || {}),
      conformite: {
        ...payload?.conformite,
        directeur_recommandation: recommendation,
      },
    };

    setStatusUpdating(true);
    try {
      await updateGrilleData(selectedPlan.id, updatedPayload, statut);
      showNotice(statut === 'approuvé' ? 'Plan approuve avec succes.' : 'Plan refuse avec succes.', 'success');
      setRefreshToken((current) => current + 1);
    } catch (err) {
      showNotice('Erreur lors de la mise a jour du plan.', 'error');
      console.error(err);
    } finally {
      setStatusUpdating(false);
    }
  };

  const getStatusBadge = (statusKey) => {
    const displayStatus = toDaDisplayStatus(statusKey);
    const statuses = {
      analyse: { label: 'En analyse', class: 'status-analysis' },
      'validé': { label: 'Validé', class: 'status-validated' },
      'non validé': { label: 'Non validé', class: 'status-rejected' },
      approuvé: { label: 'Approuvé', class: 'status-approved' },
      'non approuvé': { label: 'Refusé', class: 'status-rejected' },
    };
    const status = statuses[displayStatus] || { label: displayStatus, class: 'status-pending' };
    return <span className={`status-badge ${status.class}`}>{status.label}</span>;
  };

  const renderTimeline = () => {
    if (!timelinePlan) return null;

    const conformite = timelinePlanData?.payload?.conformite || {};
    const isChefPlan = !timelinePlan.chef_equipe_nom;
    const steps = isChefPlan
      ? [
          { label: 'Plan Soumis par le Chef d’équipe', completed: true, date: timelinePlan.date_soumission, icon: '✓' },
          {
            label: 'Révision par le Conseiller pédagogique',
            completed: conformite.conseiller_pedagogique === 'oui',
            current: !conformite.conseiller_pedagogique,
            icon: conformite.conseiller_pedagogique === 'oui' ? '✓' : conformite.conseiller_pedagogique === 'non' ? '✗' : '○',
            isActuel: !conformite.conseiller_pedagogique,
          },
          {
            label: 'Approbation par la Direction adjointe',
            completed: conformite.directeur_adjoint === 'oui',
            pending: !conformite.directeur_adjoint,
            icon: conformite.directeur_adjoint === 'oui' ? '✓' : conformite.directeur_adjoint === 'non' ? '✗' : '○',
          },
        ]
      : [
          { label: 'Plan Soumis par l’enseignant', completed: true, date: timelinePlan.date_soumission, icon: '✓' },
          {
            label: 'Révision par le Chef d’équipe',
            completed: conformite.chef_equipe === 'oui',
            current: !conformite.chef_equipe,
            icon: conformite.chef_equipe === 'oui' ? '✓' : conformite.chef_equipe === 'non' ? '✗' : '○',
            isActuel: !conformite.chef_equipe,
          },
          {
            label: 'Approbation par la Direction adjointe',
            completed: conformite.directeur_adjoint === 'oui',
            pending: !conformite.directeur_adjoint,
            icon: conformite.directeur_adjoint === 'oui' ? '✓' : conformite.directeur_adjoint === 'non' ? '✗' : '○',
          },
        ];

    return (
      <div className="timeline-modal-overlay" onClick={handleCloseTimeline}>
        <div className="timeline-modal" onClick={(event) => event.stopPropagation()}>
          <div className="timeline-header">
            <h3>Chronologie du processus</h3>
            <button className="btn-close-timeline" onClick={handleCloseTimeline} type="button">✕</button>
          </div>
          <div className="timeline-plan-info">
            <h4>{timelinePlan.titre}</h4>
            <p><strong>Cours:</strong> {timelinePlan.numero_cours}</p>
            <p><strong>Enseignant:</strong> {timelinePlan.enseignant_nom || '—'}</p>
            <p><strong>Chef d’équipe:</strong> {timelinePlan.chef_equipe_nom || '—'}</p>
            <p><strong>Statut:</strong> {getStatusBadge(timelinePlan.statusKey)}</p>
          </div>
          <div className="timeline-steps">
            {steps.map((step, index) => (
              <div key={index} className={`timeline-item ${step.completed ? 'completed' : ''} ${step.current ? 'current' : ''} ${step.pending ? 'pending' : ''} ${step.isActuel ? 'actuel' : ''}`}>
                <div className="timeline-icon">{step.icon}</div>
                <div className="timeline-content">
                  <div className="timeline-label">{step.label}</div>
                  {step.date && <div className="timeline-date">{new Date(step.date).toLocaleDateString()}</div>}
                  {step.isActuel && <div className="timeline-badge actuel-badge">Actuel</div>}
                  {step.pending && !step.current && <div className="timeline-badge pending-step">À venir</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const DA_STATUS_CONFIG = [
    { key: 'analyse', label: 'En analyse', color: '#94a3b8' },
    { key: 'non validé', label: 'Non validé', color: '#64748b' },
    { key: 'validé', label: 'Validé', color: '#3b82f6' },
    { key: 'approuvé', label: 'Approuvé', color: '#059669' },
    { key: 'non approuvé', label: 'Refusé', color: '#ef4444' },
  ];

  const renderStats = () => {
    const countByStatus = (statusKey) => plans.filter((p) => toDaDisplayStatus(p.statusKey) === statusKey).length;
    const totalPlans = plans.length;
    const plansAApprouver = countByStatus('validé');
    const plansApprouves = countByStatus('approuvé');
    const plansNonApprouves = countByStatus('non approuvé');
    const decisionsTotal = plansApprouves + plansNonApprouves;
    const tauxValidation = decisionsTotal > 0 ? Math.round((plansApprouves / decisionsTotal) * 100) : 0;
    const gaugeLength = 157;

    const donutData = DA_STATUS_CONFIG
      .map((s) => ({ label: s.label, value: countByStatus(s.key), color: s.color }))
      .filter((item) => item.value > 0);

    const pipelineData = DA_STATUS_CONFIG.map((s) => ({
      label: s.label,
      value: countByStatus(s.key),
      color: s.color,
    }));

    return (
      <section className="da-stats-section">
        <div className="da-stats-kpis">
          <div className="da-kpi-card">
            <span className="da-kpi-value">{totalPlans}</span>
            <span className="da-kpi-label">Total plans</span>
          </div>
          <div className="da-kpi-card da-kpi-blue">
            <span className="da-kpi-value">{plansAApprouver}</span>
            <span className="da-kpi-label">Plans à approuver</span>
          </div>
          <div className="da-kpi-card da-kpi-green">
            <span className="da-kpi-value">{plansApprouves}</span>
            <span className="da-kpi-label">Plans approuvés</span>
          </div>
          <div className="da-kpi-card da-kpi-red">
            <span className="da-kpi-value">{plansNonApprouves}</span>
            <span className="da-kpi-label">Plans refusés</span>
          </div>
        </div>

        <div className="da-stats-charts-row">
          <DonutChart
            title="Répartition des plans"
            subtitle="Volume de plans par statut"
            data={donutData}
          />
          <VerticalBarChart
            title="Pipeline de validation"
            subtitle="Vue synthèse du flux de traitement"
            data={pipelineData}
          />
        </div>

        <div className="da-stats-bottom-row">
          <section className="admin-graph-card da-taux-card">
            <div className="admin-graph-header">
              <h3>Taux de validation</h3>
              <p>Proportion de plans approuvés sur les décisions prises</p>
            </div>
            <div className="da-taux-content">
              <div className="da-taux-gauge">
                <svg viewBox="0 0 120 70" className="da-gauge-svg" aria-hidden="true">
                  <path d="M10 65 A50 50 0 0 1 110 65" fill="none" stroke="#e5e7eb" strokeWidth="12" strokeLinecap="round" />
                  <path
                    d="M10 65 A50 50 0 0 1 110 65"
                    fill="none"
                    stroke="#059669"
                    strokeWidth="12"
                    strokeLinecap="round"
                    strokeDasharray={`${(tauxValidation / 100) * gaugeLength} ${gaugeLength}`}
                  />
                </svg>
                <div className="da-gauge-label">
                  <strong>{tauxValidation}%</strong>
                  <span>taux d'approbation</span>
                </div>
              </div>
              <div className="da-taux-details">
                <div className="da-taux-row">
                  <span className="da-taux-dot" style={{ background: '#059669' }} />
                  <span>Approuvés</span>
                  <strong>{plansApprouves}</strong>
                </div>
                <div className="da-taux-row">
                  <span className="da-taux-dot" style={{ background: '#ef4444' }} />
                  <span>Refusés</span>
                  <strong>{plansNonApprouves}</strong>
                </div>
                <div className="da-taux-row">
                  <span className="da-taux-dot" style={{ background: '#94a3b8' }} />
                  <span>En attente</span>
                  <strong>{totalPlans - decisionsTotal}</strong>
                </div>
              </div>
            </div>
          </section>

          <section className="admin-graph-card da-statut-card">
            <div className="admin-graph-header">
              <h3>Plans par statut</h3>
              <p>Détail par étape du processus</p>
            </div>
            <table className="da-statut-table">
              <thead>
                <tr>
                  <th>Statut</th>
                  <th>Nombre</th>
                  <th>Part</th>
                </tr>
              </thead>
              <tbody>
                {DA_STATUS_CONFIG.map((s) => {
                  const count = countByStatus(s.key);
                  const pct = totalPlans > 0 ? Math.round((count / totalPlans) * 100) : 0;
                  return (
                    <tr key={s.key}>
                      <td>
                        <span className="da-status-dot" style={{ background: s.color }} />
                        {s.label}
                      </td>
                      <td><strong>{count}</strong></td>
                      <td>
                        <div className="da-pct-bar-track">
                          <div className="da-pct-bar-fill" style={{ width: `${pct}%`, background: s.color }} />
                        </div>
                        <span className="da-pct-label">{pct}%</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        </div>
      </section>
    );
  };

  return (
    <div className="directeur-dashboard">
      <Sidebar active={activeSection} onNavigate={handleNavigate} />

      {activeSection === 'roles' ? (
        <div className="directeur-main roles-section">
          <DirecteurRoleManagement />
        </div>
      ) : activeSection === 'stats' ? (
        <section className="directeur-main">
          <header className="dashboard-header">
            <div className="header-brand-group">
              <img className="header-brand-icon" src={BRAND_ASSETS.iconMini} alt="" aria-hidden="true" />
              <div>
                <h2>Tableau de bord — Direction Adjointe</h2>
                <p className="muted">Vue d'ensemble des statistiques et indicateurs de validation.</p>
              </div>
            </div>
            <div className="header-user">
              <button className="directeur-refresh-btn" onClick={handleRefresh} type="button" disabled={loading}>
                {loading ? 'Rafraîchissement...' : 'Rafraîchir'}
              </button>
              <span>{user?.nom || 'Direction'}</span>
              <div className="avatar">{user?.nom?.slice(0, 1) || 'D'}</div>
              <button className="directeur-logout-btn" onClick={logout} type="button">Deconnexion</button>
            </div>
          </header>
          {loading ? (
            <p className="loading">Chargement des statistiques...</p>
          ) : (
            renderStats()
          )}
        </section>
      ) : (
        <section className="directeur-main">
          <header className="dashboard-header">
            <div className="header-brand-group">
              <img className="header-brand-icon" src={BRAND_ASSETS.iconMini} alt="" aria-hidden="true" />
              <div>
                <h2>Direction Adjointe - Approbation des Plans</h2>
                <p className="muted">Consulter et compléter l'approbation finale des plans.</p>
              </div>
            </div>
            <div className="header-user">
              <button className="directeur-refresh-btn" onClick={handleRefresh} type="button" disabled={loading}>
                {loading ? 'Rafraîchissement...' : 'Rafraîchir'}
              </button>
              <span>{user?.nom || 'Direction'}</span>
              <div className="avatar">{user?.nom?.slice(0, 1) || 'D'}</div>
              <button className="directeur-logout-btn" onClick={logout} type="button">Deconnexion</button>
            </div>
          </header>

          <div className="dashboard-stats">
            <div className="stat-card">
              <h3>{filteredPlans.filter((plan) => plan.statusKey === 'validé').length}</h3>
              <p>Plans à approuver</p>
            </div>
            <div className="stat-card">
              <h3>{filteredPlans.filter((plan) => plan.statusKey === 'approuvé').length}</h3>
              <p>Plans approuvés</p>
            </div>
            <div className="stat-card">
              <h3>{filteredPlans.filter((plan) => plan.statusKey === 'non approuvé').length}</h3>
              <p>Plans refusés</p>
            </div>
          </div>

          <div className="dashboard-filters">
            <div className="filter-group">
              <label>Enseignant</label>
              <input type="text" value={filters.teacher} onChange={(event) => handleFilterChange('teacher', event.target.value)} placeholder="Rechercher par enseignant..." />
            </div>
            <div className="filter-group">
              <label>Cours</label>
              <input type="text" value={filters.course} onChange={(event) => handleFilterChange('course', event.target.value)} placeholder="Rechercher par cours..." />
            </div>
            <div className="filter-group">
              <label>Chef d’équipe</label>
              <input type="text" value={filters.chef} onChange={(event) => handleFilterChange('chef', event.target.value)} placeholder="Rechercher par chef d’équipe..." />
            </div>
            <div className="filter-group">
              <label>Statut</label>
              <select value={filters.status} onChange={(event) => handleFilterChange('status', event.target.value)}>
                <option value="">Tous les statuts</option>
                <option value="validé">Validé</option>
                <option value="non validé">Non validé</option>
                <option value="approuvé">Approuvé</option>
                <option value="non approuvé">Refusé</option>
                <option value="analyse">En analyse</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Session</label>
              <select value={filters.session} onChange={(event) => handleFilterChange('session', event.target.value)}>
                <option value="">Toutes les sessions</option>
                {sessions.filter((value) => value !== 'Toutes').map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Programme</label>
              <select value={filters.programme} onChange={(event) => handleFilterChange('programme', event.target.value)}>
                <option value="">Tous les programmes</option>
                {programmes.filter((value) => value !== 'Tous').map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Mode</label>
              <select value={filters.mode} onChange={(event) => handleFilterChange('mode', event.target.value)}>
                <option value="">Tous les modes</option>
                {modes.filter((value) => value !== 'Tous').map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
            <div className="filter-group">
              <label>Campus</label>
              <select value={filters.campus} onChange={(event) => handleFilterChange('campus', event.target.value)}>
                <option value="">Tous les campus</option>
                {campus.filter((value) => value !== 'Tous').map((value) => <option key={value} value={value}>{value}</option>)}
              </select>
            </div>
          </div>

          {loading ? (
            <p className="loading">Chargement des plans...</p>
          ) : error ? (
            <p className="error-message">{error}</p>
          ) : (
            <>
              <section className="plans-list">
                {filteredPlans.length === 0 ? (
                  <div className="no-data">Aucun plan à afficher</div>
                ) : (
                  <div className="plans-table-container">
                    <table className="plans-table">
                      <thead>
                        <tr>
                          <th><span className="sort-label">Cours <span className="sort-indicator" aria-hidden="true">{getSortIndicator('titre')}</span></span></th>
                          <th><span className="sort-label">Enseignant <span className="sort-indicator" aria-hidden="true">{getSortIndicator('enseignant_nom')}</span></span></th>
                          <th><span className="sort-label">Chef d’équipe <span className="sort-indicator" aria-hidden="true">{getSortIndicator('chef_equipe_nom')}</span></span></th>
                          <th><span className="sort-label">Session <span className="sort-indicator" aria-hidden="true">{getSortIndicator('session')}</span></span></th>
                          <th><span className="sort-label">Programme <span className="sort-indicator" aria-hidden="true">{getSortIndicator('programme')}</span></span></th>
                          <th><span className="sort-label">Mode <span className="sort-indicator" aria-hidden="true">{getSortIndicator('mode_enseignement')}</span></span></th>
                          <th><span className="sort-label">Campus <span className="sort-indicator" aria-hidden="true">{getSortIndicator('campus')}</span></span></th>
                          <th><span className="sort-label">Date de soumission <span className="sort-indicator" aria-hidden="true">{getSortIndicator('date_soumission')}</span></span></th>
                          <th><span className="sort-label">Statut <span className="sort-indicator" aria-hidden="true">{getSortIndicator('statusKey')}</span></span></th>
                          <th>Actions</th>
                        </tr>
                        <tr className="sort-row">
                          <th>
                            <select value={filters.sortBy === 'titre' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('titre', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="asc">A-Z</option>
                              <option value="desc">Z-A</option>
                            </select>
                          </th>
                          <th>
                            <select value={filters.sortBy === 'enseignant_nom' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('enseignant_nom', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="asc">A-Z</option>
                              <option value="desc">Z-A</option>
                            </select>
                          </th>
                          <th>
                            <select value={filters.sortBy === 'chef_equipe_nom' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('chef_equipe_nom', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="asc">A-Z</option>
                              <option value="desc">Z-A</option>
                            </select>
                          </th>
                          <th>
                            <select value={filters.sortBy === 'session' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('session', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="asc">A-Z</option>
                              <option value="desc">Z-A</option>
                            </select>
                          </th>
                          <th>
                            <select value={filters.sortBy === 'programme' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('programme', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="asc">A-Z</option>
                              <option value="desc">Z-A</option>
                            </select>
                          </th>
                          <th>
                            <select value={filters.sortBy === 'mode_enseignement' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('mode_enseignement', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="asc">A-Z</option>
                              <option value="desc">Z-A</option>
                            </select>
                          </th>
                          <th>
                            <select value={filters.sortBy === 'campus' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('campus', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="asc">A-Z</option>
                              <option value="desc">Z-A</option>
                            </select>
                          </th>
                          <th>
                            <select value={filters.sortBy === 'date_soumission' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('date_soumission', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="desc">Plus récent</option>
                              <option value="asc">Plus ancien</option>
                            </select>
                          </th>
                          <th>
                            <select value={filters.sortBy === 'statusKey' ? filters.sortDirection : ''} onChange={(e) => handleSortChange('statusKey', e.target.value)}>
                              <option value="">Tri</option>
                              <option value="asc">Priorité haute</option>
                              <option value="desc">Priorité basse</option>
                            </select>
                          </th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedPlans.map((plan) => (
                          <tr
                            key={plan.id}
                            className={selectedPlan?.id === plan.id ? 'selected-row' : ''}
                            onClick={() => handleSelectPlan(plan)}
                            onKeyDown={(event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                handleSelectPlan(plan);
                              }
                            }}
                            tabIndex={0}
                            role="button"
                            aria-label={`Sélectionner le plan ${plan.numero_cours || plan.titre || plan.id}`}
                          >
                            <td>
                              <div className="course-info">
                                <strong>{plan.numero_cours}</strong>
                                <span className="course-title">{plan.titre}</span>
                              </div>
                            </td>
                            <td>{plan.enseignant_nom || '—'}</td>
                            <td>{plan.chef_equipe_nom || '—'}</td>
                            <td>{plan.session || '—'}</td>
                            <td>{plan.programme || '—'}</td>
                            <td>{plan.mode_enseignement || '—'}</td>
                            <td>{plan.campus || '—'}</td>
                            <td>{plan.date_soumission ? new Date(plan.date_soumission).toLocaleDateString() : '—'}</td>
                            <td>{getStatusBadge(plan.statusKey)}</td>
                            <td>
                              <div className="action-buttons">
                                <button className="btn-view-pdf" type="button" onClick={(event) => { event.stopPropagation(); handleOpenPreview(plan); }}>
                                  📄 PDF
                                </button>
                                <button className="btn-open-timeline" type="button" onClick={(event) => { event.stopPropagation(); handleOpenTimeline(plan); }}>
                                  🕓 Chronologie
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    <PaginationControls currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
                  </div>
                )}
              </section>

              <section className="directeur-validation-section">
                <div className="directeur-validation-header">
                  <div>
                    <h3>Formulaire de validation de la direction adjointe</h3>
                    <p>
                      {selectedPlan
                        ? 'Complétez la grille du plan sélectionné ci-dessous.'
                        : 'Sélectionnez un plan dans la file pour préremplir le formulaire de décision.'}
                    </p>
                  </div>
                  {selectedPlan && (
                    <button type="button" className="directeur-selection-chip" onClick={() => setSelectedPlan(null)}>
                      Fermer le formulaire
                    </button>
                  )}
                </div>

                <ValidationInterface
                  plan={selectedPlan ? {
                    id: selectedPlan.id,
                    title: selectedPlan.titre || selectedPlan.numero_cours || selectedPlan.cours,
                    course: selectedPlan.numero_cours || selectedPlan.cours || 'N/A',
                    teacher: selectedPlan.enseignant_nom || '—',
                    statusLabel: selectedPlan.statusKey || 'validé',
                  } : null}
                  onStatusChange={handleStatusChange}
                  statusUpdating={statusUpdating}
                  onSaveGrid={handleSaveGrid}
                  initialPayload={planPayload}
                  loadingPayload={loadingPayload}
                  verifierName={user?.nom || ''}
                  validationRole="directeur"
                  saveButtonLabel="💾 Enregistrer la décision de la direction"
                  headerTitle="Formulaire de validation de la direction adjointe"
                  headerSubtitle="Sélectionnez un plan dans la file pour compléter l’approbation finale."
                  hideResultButton={true}
                />
              </section>
            </>
          )}
        </section>
      )}

      {renderTimeline()}
      <PlanPreviewModal plan={previewPlan} onClose={() => setPreviewPlan(null)} />
      <NoticeToast notice={notice} onClose={clearNotice} />
    </div>
  );
}
